import { useEffect, useRef, useState } from "react";
import {
  DISPLAY_PHASES,
  WALL_OWNER_PROJECT_MAX_LENGTH,
  type DisplayPhase,
  type IdentifyOverlay,
  type WallOwner,
  type WallSnapshot
} from "../../../src/display/types";

export type DeviceDisplayStatus =
  | "connecting"
  | "live"
  | "reconnecting"
  | "offline"
  | "inactive"
  | "expired";

export interface DeviceDisplayState {
  status: DeviceDisplayStatus;
  snapshot: WallSnapshot | null;
  identify?: IdentifyOverlay | null;
}

interface ActiveIdentify {
  readonly key: string;
  readonly token: symbol;
  readonly overlay: IdentifyOverlay;
  readonly authoritative: boolean;
  readonly deadline: number;
  readonly acknowledgement: AbortController | null;
}

const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const CAPABILITY_PATTERN = /^[A-Za-z0-9_-]{43}$/;
const BACKOFF_MS = [1_000, 2_000, 4_000, 8_000, 16_000, 30_000] as const;
const OFFLINE_AFTER_FAILURES = 3;
const FETCH_DEADLINE_MS = 10_000;
const SSE_LIVENESS_MS = 45_000;
const MAX_SSE_BUFFER = 262_144;
const MAX_EVENT_DATA = 131_072;
const MAX_TIMEOUT = 2_147_483_647;
const phaseSet: ReadonlySet<string> = new Set<string>(DISPLAY_PHASES);

function boundedString(value: unknown, max: number): string | null {
  return typeof value === "string" &&
    value.length > 0 &&
    value.length <= max &&
    !/[\r\n]/.test(value)
    ? value
    : null;
}

function boundedTime(value: unknown): string | null {
  if (typeof value !== "string" || value.length > 64) return null;
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? new Date(parsed).toISOString() : null;
}

// Nullable display fields: absent or null → null; anything else must be a
// bounded string, otherwise `undefined` signals a malformed snapshot.
function nullableString(value: unknown, max: number): string | null | undefined {
  if (value == null) return null;
  return boundedString(value, max) ?? undefined;
}

function validateOwner(value: unknown): WallOwner | null {
  if (typeof value !== "object" || value === null) return null;
  const owner = value as Record<string, unknown>;
  if (owner.kind === "human") {
    const email = boundedString(owner.email, 320);
    return email ? Object.freeze({ kind: "human" as const, email }) : null;
  }
  if (owner.kind === "automation") {
    const project =
      owner.project == null
        ? undefined
        : boundedString(owner.project, WALL_OWNER_PROJECT_MAX_LENGTH);
    const job = owner.job == null ? undefined : boundedString(owner.job, 256);
    if (owner.project != null && project === null) return null;
    if (owner.job != null && job === null) return null;
    return Object.freeze({
      kind: "automation" as const,
      ...(project ? { project } : {}),
      ...(job ? { job } : {})
    });
  }
  if (owner.kind === "unassigned") {
    return Object.freeze({ kind: "unassigned" as const });
  }
  return null;
}

function validateIdentify(value: unknown): IdentifyOverlay | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const v = value as Record<string, unknown>;
  const requestId = boundedString(v.requestId, 128);
  const serial = boundedString(v.serial, 128);
  const model = boundedString(v.model, 256);
  const osVersion = boundedString(v.osVersion, 128);
  const expiresAt = boundedTime(v.expiresAt);
  const visibleUntil = v.visibleUntil === null ? null : boundedTime(v.visibleUntil);
  const wallLocation = v.wallLocation == null ? undefined : boundedString(v.wallLocation, 256);
  if (!requestId || !serial || !model || !osVersion || !expiresAt ||
      (v.visibleUntil !== null && !visibleUntil) ||
      (v.wallLocation != null && !wallLocation) ||
      !Number.isSafeInteger(v.revision) || (v.revision as number) < 0) return null;
  return Object.freeze({ requestId, revision: v.revision as number, serial, model,
    osVersion, ...(wallLocation ? { wallLocation } : {}), expiresAt, visibleUntil });
}

export function validateWallSnapshot(
  value: unknown,
  sessionId: string
): WallSnapshot | null {
  if (typeof value !== "object" || value === null || Array.isArray(value))
    return null;
  const v = value as Record<string, unknown>;
  if (v.sessionId !== sessionId) return null;
  if (!Number.isSafeInteger(v.generation) || (v.generation as number) < 1)
    return null;
  if (!Number.isSafeInteger(v.revision) || (v.revision as number) < 0)
    return null;
  const serial = boundedString(v.serial, 128);
  const updatedAt = boundedTime(v.updatedAt);
  const expiresAt = boundedTime(v.expiresAt);
  if (
    serial === null ||
    updatedAt === null ||
    expiresAt === null ||
    typeof v.phase !== "string" ||
    !phaseSet.has(v.phase)
  )
    return null;
  const owner = validateOwner(v.owner);
  if (owner === null) return null;
  const app = v.app === null ? null : boundedString(v.app, 128);
  const runId = v.runId === null ? null : boundedString(v.runId, 128);
  if ((v.app !== null && app === null) || (v.runId !== null && runId === null))
    return null;
  const deviceId =
    typeof v.deviceId === "string" && UUID_PATTERN.test(v.deviceId)
      ? v.deviceId
      : null;
  const hostName = nullableString(v.hostName, 253);
  const model = nullableString(v.model, 256);
  const osVersion = nullableString(v.osVersion, 128);
  if (
    deviceId === null ||
    hostName === undefined ||
    model === undefined ||
    osVersion === undefined
  )
    return null;
  let download: WallSnapshot["download"] = null;
  if (v.download != null) {
    if (typeof v.download !== "object" || Array.isArray(v.download))
      return null;
    const d = v.download as Record<string, unknown>;
    if (!Number.isSafeInteger(d.bytes) || (d.bytes as number) < 0) return null;
    if (
      d.total != null &&
      (!Number.isSafeInteger(d.total) || (d.total as number) <= 0)
    )
      return null;
    if (d.total != null && (d.bytes as number) > (d.total as number))
      return null;
    download = Object.freeze({
      bytes: d.bytes as number,
      total: d.total == null ? null : (d.total as number)
    });
  }
  const identify = v.identify == null ? undefined : validateIdentify(v.identify);
  if (v.identify != null && identify === null) return null;
  return Object.freeze({
    sessionId,
    generation: v.generation as number,
    revision: v.revision as number,
    serial,
    deviceId,
    hostName,
    model,
    osVersion,
    owner,
    app,
    runId,
    phase: v.phase as DisplayPhase,
    updatedAt,
    expiresAt,
    download,
    ...(identify ? { identify } : {})
  });
}

function stripFragment(): boolean {
  try {
    window.history.replaceState(
      window.history.state,
      "",
      `${window.location.pathname}${window.location.search}`
    );
    return window.location.hash === "";
  } catch {
    return false;
  }
}

function readCapability(sessionId: string): string | null {
  const hash = window.location.hash;
  const fragment = hash.startsWith("#") ? hash.slice(1) : "";
  if (hash.length > 0 && !stripFragment()) return null;
  if (!UUID_PATTERN.test(sessionId) || !CAPABILITY_PATTERN.test(fragment))
    return null;
  return fragment;
}

export function useDeviceDisplay(sessionId: string): DeviceDisplayState {
  const capabilityRef = useRef<string | null | undefined>(undefined);
  if (capabilityRef.current === undefined) {
    capabilityRef.current = readCapability(sessionId);
  }
  const [state, setState] = useState<DeviceDisplayState>({
    status: "inactive",
    snapshot: null,
    identify: null
  });

  useEffect(() => {
    const capability = capabilityRef.current;
    if (capability === null) {
      setState({ status: "inactive", snapshot: null, identify: null });
      return;
    }

    let disposed = false;
    let terminal = false;
    let failures = 0;
    let generation: number | null = null;
    let revision = -1;
    let snapshot: WallSnapshot | null = null;
    let identify: IdentifyOverlay | null = null;
    let currentStatus: DeviceDisplayStatus = "inactive";
    let activeIdentify: ActiveIdentify | null = null;
    let identifyTimer: ReturnType<typeof setTimeout> | null = null;
    let backoffTimer: ReturnType<typeof setTimeout> | null = null;
    let expiryTimer: ReturnType<typeof setTimeout> | null = null;
    let wake: (() => void) | null = null;
    let activeRequest: AbortController | null = null;
    let retryImmediately = false;
    const lifecycleController = new AbortController();

    const publish = (
      status: DeviceDisplayStatus,
      next: WallSnapshot | null
    ): void => {
      snapshot = next;
      currentStatus = status;
      if (!disposed) setState({ status, snapshot: next, identify });
    };

    const clearTimers = (): void => {
      if (backoffTimer) clearTimeout(backoffTimer);
      if (expiryTimer) clearTimeout(expiryTimer);
      if (identifyTimer) clearTimeout(identifyTimer);
      backoffTimer = null;
      expiryTimer = null;
      identifyTimer = null;
    };

    const invalidateIdentify = (): void => {
      if (identifyTimer) clearTimeout(identifyTimer);
      identifyTimer = null;
      activeIdentify?.acknowledgement?.abort();
      activeIdentify = null;
      identify = null;
      publish(currentStatus, snapshot);
    };

    const firstSeenDeadline = (key: string): number => {
      const storageKey = `device-hive:identify:${sessionId}:${key}`;
      let existing = Number.NaN;
      try { existing = Number(localStorage.getItem(storageKey)); } catch { /* memory-only fallback */ }
      if (Number.isFinite(existing) && existing > 0) return existing;
      const deadline = Date.now() + 10_000;
      try { localStorage.setItem(storageKey, String(deadline)); } catch { /* memory-only fallback */ }
      return deadline;
    };

    const showIdentify = (active: ActiveIdentify): void => {
      if (identifyTimer) clearTimeout(identifyTimer);
      const delay = active.deadline - Date.now();
      if (delay <= 0) { invalidateIdentify(); return; }
      activeIdentify = active;
      identify = active.overlay;
      publish("live", snapshot);
      const token = active.token;
      identifyTimer = setTimeout(() => {
        if (activeIdentify?.token === token) invalidateIdentify();
      }, Math.min(delay, MAX_TIMEOUT));
    };

    const handleIdentify = (incoming: WallSnapshot): void => {
      const overlay = incoming.identify ?? null;
      if (!overlay) { if (activeIdentify) invalidateIdentify(); return; }
      const key = `${incoming.generation}:${overlay.requestId}:${overlay.revision}`;
      const localDeadline = firstSeenDeadline(key);
      if (key === activeIdentify?.key) {
        if (overlay.visibleUntil && !activeIdentify.authoritative) {
          activeIdentify.acknowledgement?.abort();
          showIdentify({ key, token: Symbol(key), overlay, authoritative: true,
            deadline: Math.min(Date.parse(overlay.visibleUntil), localDeadline), acknowledgement: null });
        }
        return;
      }
      if (activeIdentify) invalidateIdentify();
      const token = Symbol(key);
      if (overlay.visibleUntil) {
        showIdentify({ key, token, overlay, authoritative: true,
          deadline: Math.min(Date.parse(overlay.visibleUntil), localDeadline), acknowledgement: null });
        return;
      }
      const acknowledgement = new AbortController();
      const abortAcknowledgement = () => acknowledgement.abort();
      lifecycleController.signal.addEventListener("abort", abortAcknowledgement, { once: true });
      const fallback = Math.min(localDeadline, Date.parse(overlay.expiresAt));
      const active: ActiveIdentify = { key, token, overlay, authoritative: false,
        deadline: fallback, acknowledgement };
      showIdentify(active);
      if (activeIdentify?.token !== token) {
        lifecycleController.signal.removeEventListener("abort", abortAcknowledgement);
        acknowledgement.abort();
        return;
      }
      void (async () => {
        const deadline = setTimeout(() => acknowledgement.abort(), FETCH_DEADLINE_MS);
        try {
          const response = await fetch(`/display-api/sessions/${encodeURIComponent(sessionId)}/identify/rendered`, {
            method: "POST",
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${capability}` },
            credentials: "omit", cache: "no-store", referrerPolicy: "no-referrer",
            body: JSON.stringify({ request_id: overlay.requestId, generation: incoming.generation, revision: overlay.revision }),
            signal: acknowledgement.signal
          });
          if (disposed || acknowledgement.signal.aborted || activeIdentify?.token !== token) return;
          if ([401, 409, 410].includes(response.status)) { invalidateIdentify(); return; }
          if (!response.ok) return;
          const body = await Promise.race([
            response.json() as Promise<{ visibleUntil?: unknown }>,
            new Promise<never>((_resolve, reject) => acknowledgement.signal.addEventListener(
              "abort", () => reject(new DOMException("Aborted", "AbortError")), { once: true }
            ))
          ]);
          if (disposed || acknowledgement.signal.aborted || activeIdentify?.token !== token) return;
          const visibleUntil = boundedTime(body.visibleUntil);
          if (!visibleUntil) return;
          showIdentify({ key, token, overlay: { ...overlay, visibleUntil }, authoritative: true,
            deadline: Math.min(Date.parse(visibleUntil), localDeadline), acknowledgement: null });
        } catch {
          // The persisted first-seen fallback remains authoritative on transport failure.
        } finally {
          clearTimeout(deadline);
          lifecycleController.signal.removeEventListener("abort", abortAcknowledgement);
          if (activeIdentify?.token === token && activeIdentify.acknowledgement === acknowledgement) {
            activeIdentify = { ...activeIdentify, acknowledgement: null };
          }
        }
      })();
    };

    const finishTerminal = (): void => {
      if (terminal || disposed) return;
      terminal = true;
      clearTimers();
      invalidateIdentify();
      publish("expired", null);
      lifecycleController.abort();
    };

    const scheduleExpiry = (expiresAt: string): boolean => {
      const delay = new Date(expiresAt).getTime() - Date.now();
      if (delay <= 0) {
        finishTerminal();
        return false;
      }
      if (expiryTimer) clearTimeout(expiryTimer);
      expiryTimer = setTimeout(
        () => {
          scheduleExpiry(expiresAt);
        },
        Math.min(delay, MAX_TIMEOUT)
      );
      return true;
    };

    const adopt = (incoming: WallSnapshot): boolean => {
      if (generation === null) generation = incoming.generation;
      if (incoming.generation !== generation) {
        if (activeIdentify) invalidateIdentify();
        return false;
      }
      if (incoming.revision < revision) return false;
      if (incoming.revision > revision) {
        revision = incoming.revision;
        publish("live", incoming);
        handleIdentify(incoming);
        if (!scheduleExpiry(incoming.expiresAt)) return false;
      } else {
        handleIdentify(incoming);
      }
      return true;
    };

    const request = async <T>(
      url: string,
      accept: string,
      deadlineThroughBody: boolean,
      consume: (response: Response, signal: AbortSignal) => Promise<T>
    ): Promise<T> => {
      const requestController = new AbortController();
      activeRequest = requestController;
      const abortRequest = () => requestController.abort();
      lifecycleController.signal.addEventListener("abort", abortRequest, {
        once: true
      });
      let deadline: ReturnType<typeof setTimeout> | null = setTimeout(
        abortRequest,
        FETCH_DEADLINE_MS
      );
      try {
        const response = await fetch(url, {
          method: "GET",
          headers: { Accept: accept, Authorization: `Bearer ${capability}` },
          credentials: "omit",
          cache: "no-store",
          referrerPolicy: "no-referrer",
          signal: requestController.signal
        });
        if (
          disposed ||
          terminal ||
          lifecycleController.signal.aborted ||
          requestController.signal.aborted
        ) {
          requestController.abort();
          throw new DOMException("Display lifecycle ended", "AbortError");
        }
        if (!deadlineThroughBody && deadline) {
          clearTimeout(deadline);
          deadline = null;
        }
        return await consume(response, requestController.signal);
      } finally {
        if (deadline) clearTimeout(deadline);
        lifecycleController.signal.removeEventListener("abort", abortRequest);
        if (activeRequest === requestController) activeRequest = null;
        requestController.abort();
      }
    };

    const sleep = (ms: number): Promise<void> =>
      new Promise((resolve) => {
        wake = () => {
          if (backoffTimer) clearTimeout(backoffTimer);
          backoffTimer = null;
          wake = null;
          resolve();
        };
        backoffTimer = setTimeout(() => {
          if (wake) wake();
        }, ms);
      });

    const dispatchEvent = (
      eventType: string,
      data: string
    ): "expired" | "ok" | null => {
      if (eventType === "expired") return "expired";
      if (eventType !== "snapshot" && eventType !== "update") return null;
      let payload: unknown;
      try {
        payload = JSON.parse(data);
      } catch {
        return null;
      }
      const candidate = validateWallSnapshot(payload, sessionId);
      if (candidate === null) return null;
      if (generation !== null && candidate.generation !== generation) {
        if (activeIdentify) invalidateIdentify();
        return null;
      }
      if (candidate.revision < revision) return null;
      // A valid event proves the complete snapshot + SSE chain is healthy,
      // including the stream's expected duplicate opening snapshot.
      failures = 0;
      if (candidate.revision === revision) {
        handleIdentify(candidate);
        return "ok";
      }
      adopt(candidate);
      return "ok";
    };

    const readStream = async (
      body: ReadableStream<Uint8Array>,
      signal: AbortSignal
    ): Promise<"expired" | "retry"> => {
      const reader = body.getReader();
      const cancelReader = () => void reader.cancel().catch(() => {});
      signal.addEventListener("abort", cancelReader, { once: true });
      const decoder = new TextDecoder("utf-8");
      let buffer = "";
      let eventType = "";
      let dataLines: string[] = [];
      let dataLength = 0;

      const nextChunk = async () => {
        let watchdog: ReturnType<typeof setTimeout> | null = null;
        try {
          return await Promise.race([
            reader.read(),
            new Promise<never>((_resolve, reject) => {
              watchdog = setTimeout(() => {
                cancelReader();
                reject(new Error("display_stream_silent"));
              }, SSE_LIVENESS_MS);
            })
          ]);
        } finally {
          if (watchdog) clearTimeout(watchdog);
        }
      };

      try {
        for (;;) {
          if (disposed || terminal) return "retry";
          const chunk = await nextChunk();
          if (disposed || terminal) return "retry";
          if (chunk.done) return "retry";
          buffer += decoder.decode(chunk.value, { stream: true });
          if (buffer.length > MAX_SSE_BUFFER)
            throw new Error("display_stream_overflow");
          for (;;) {
            const newline = buffer.indexOf("\n");
            const carriage = buffer.indexOf("\r");
            let line: string;
            let consumed: number;
            if (carriage !== -1 && (newline === -1 || carriage < newline)) {
              if (carriage === buffer.length - 1) break;
              if (buffer[carriage + 1] === "\n") {
                line = buffer.slice(0, carriage);
                consumed = carriage + 2;
              } else {
                line = buffer.slice(0, carriage);
                consumed = carriage + 1;
              }
            } else if (newline !== -1) {
              line = buffer.slice(0, newline);
              consumed = newline + 1;
            } else {
              break;
            }
            buffer = buffer.slice(consumed);
            if (line === "") {
              if (dataLines.length > 0 || eventType.length > 0) {
                const outcome = dispatchEvent(eventType, dataLines.join("\n"));
                eventType = "";
                dataLines = [];
                dataLength = 0;
                if (outcome === "expired") return "expired";
              }
              continue;
            }
            if (line.startsWith(":")) continue;
            const colon = line.indexOf(":");
            const field = colon === -1 ? line : line.slice(0, colon);
            let value = colon === -1 ? "" : line.slice(colon + 1);
            if (value.startsWith(" ")) value = value.slice(1);
            if (field === "event") eventType = value;
            else if (field === "data") {
              dataLength += value.length + 1;
              if (dataLength > MAX_EVENT_DATA)
                throw new Error("display_event_overflow");
              dataLines.push(value);
            }
          }
        }
      } finally {
        signal.removeEventListener("abort", cancelReader);
        await reader.cancel().catch(() => {});
        try {
          reader.releaseLock();
        } catch {
          // Cancellation still releases the transport; a hostile reader may
          // reject lock release while its final read settles.
        }
      }
    };

    const run = async (): Promise<void> => {
      const base = `/display-api/sessions/${encodeURIComponent(sessionId)}`;
      while (!disposed && !terminal) {
        let outcome: "expired" | "retry" = "retry";
        try {
          const candidate = await request(
            `${base}/snapshot`,
            "application/json",
            true,
            async (snapshotResponse) => {
              if (
                snapshotResponse.status === 401 ||
                snapshotResponse.status === 410
              ) {
                finishTerminal();
                return null;
              }
              if (!snapshotResponse.ok)
                throw new Error("display_snapshot_unavailable");
              return validateWallSnapshot(
                await snapshotResponse.json(),
                sessionId
              );
            }
          );
          if (disposed || terminal) return;
          if (candidate === null) throw new Error("display_snapshot_invalid");
          const adopted = adopt(candidate);
          if (disposed || terminal) return;
          if (!adopted) throw new Error("display_snapshot_stale");
          publish("live", snapshot);
          outcome = await request(
            `${base}/events`,
            "text/event-stream",
            false,
            async (eventResponse, signal) => {
              if (
                eventResponse.status === 401 ||
                eventResponse.status === 410
              ) {
                finishTerminal();
                return "retry";
              }
              if (!eventResponse.ok || !eventResponse.body)
                throw new Error("display_stream_unavailable");
              return readStream(eventResponse.body, signal);
            }
          );
        } catch {
          if (disposed || terminal || lifecycleController.signal.aborted)
            return;
        }
        if (disposed || terminal) return;
        if (outcome === "expired") {
          finishTerminal();
          return;
        }
        const delay = retryImmediately
          ? 0
          : BACKOFF_MS[Math.min(failures, BACKOFF_MS.length - 1)];
        retryImmediately = false;
        publish(
          !navigator.onLine || failures >= OFFLINE_AFTER_FAILURES
            ? "offline"
            : "reconnecting",
          snapshot
        );
        failures += 1;
        if (delay > 0) {
          await sleep(delay);
          retryImmediately = false;
        }
      }
    };

    const retryWhenOnline = (): void => {
      if (disposed || terminal) return;
      retryImmediately = true;
      activeRequest?.abort();
      if (wake) wake();
    };

    publish("connecting", null);
    window.addEventListener("online", retryWhenOnline);
    void run();

    return () => {
      disposed = true;
      window.removeEventListener("online", retryWhenOnline);
      clearTimers();
      invalidateIdentify();
      if (wake) wake();
      lifecycleController.abort();
    };
  }, [sessionId]);

  return state;
}
